import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  ErrorBanner,
  Field,
  Input,
  PageHeader,
  Textarea,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

export function DailyReportEditor() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [weather, setWeather] = useState('')
  const [manpower, setManpower] = useState('')
  const [notes, setNotes] = useState('')

  const create = useMutation({
    mutationFn: async () => {
      const epoch = Math.floor(new Date(reportDate).getTime() / 1000)
      const payload = {
        project_id: projectId,
        report_date: epoch,
        weather: weather || undefined,
        manpower_total: manpower ? Number(manpower) : undefined,
        notes: notes || undefined,
      }
      return fetchJson(orgPath('/daily-reports'), {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['daily-reports', projectId] })
      navigate('..', { relative: 'path' })
    },
  })

  return (
    <div>
      <PageHeader title="New daily report" />
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Report date">
              <Input
                type="date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                required
              />
            </Field>
            <Field label="Weather">
              <Input
                type="text"
                placeholder="e.g. clear, 32°C"
                value={weather}
                onChange={(e) => setWeather(e.target.value)}
              />
            </Field>
            <Field label="Manpower (total)">
              <Input
                type="number"
                min={0}
                value={manpower}
                onChange={(e) => setManpower(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Site activities, issues, observations…"
            />
          </Field>
          {create.isError && <ErrorBanner error={create.error} />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Save report'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
